import { AnalyticsContext } from '@atlaskit/analytics-next';
import { MarketplaceAppPaymentModelValues } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import { AppHeader, ResponsiveTabs, useFeatureFlags } from '@atlassian/mpac-ui';
import { MarketplaceAppUtils, UrlUtils } from '@atlassian/mpac-utils';
import React from 'react';
import { AppListingTab, TEST_ID } from "../../constants";
import { makeAppHeaderProps } from "../../utils";
import { HostingSelectContainer, TabAfterContentDiv } from "./AppListing.styles";
import transformAppListing from "./AppListing.transform";
import { compatibleWithCloudSupportedProduct } from "./AppListing.utils";
import { makeHeaderLozenges } from "./HeaderLozenges";
import HostingSelect from "./HostingSelect";
import ProductCompatibilitySummary from "./ProductCompatibilitySummary";
import { AppStructuredData } from "./StructuredData";
import InstallationTab from "./tabs/InstallationTab";
import OverviewTab from "./tabs/OverviewTab";
import PricingTab from "./tabs/PricingTab";
import ReviewsTab from "./tabs/ReviewsTab";
import SupportTab from "./tabs/SupportTab";
import VersionsTab from "./tabs/VersionsTab";
const MAX_VISIBLE_TABS_CONFIG = {
  xs: 2,
  sm: 4
}; // eslint-disable-next-line max-lines-per-function -- function is thin on logic

export const makeAppListingTabs = ({
  /* eslint sort-keys: "error" */
  appListing,
  appListingLink,
  selectedHosting,
  showInstallationTab,
  showIntegrationsDetailsSection,
  showResourcesSection,
  showCertificationSection,
  showReviewsTab,
  showVersionsTab,
  showPricingTab,
  mpacBaseUrl,
  atlassianCommunityBaseUrl,
  userLoggedIn,
  loginHref,
  onSupportLinkClick,
  onMediaViewerOpen,
  onWatchAppToggle,
  onSeeMoreReviewsClick,
  onArtifactDownloadClick,
  cloudMigration,
  onCloudMigrationResourceLinksClick,
  onPricingCalculatorInstallCTAClick
}) => {
  const tabs = []; // 1 => Overview Tab

  tabs.push({
    label: 'Overview',
    content: /*#__PURE__*/React.createElement(OverviewTab, {
      appListing: appListing,
      cloudMigration: cloudMigration,
      showIntegrationsDetailsSection: showIntegrationsDetailsSection,
      showResourcesSection: showResourcesSection,
      showCertificationSection: showCertificationSection,
      userLoggedIn: userLoggedIn,
      loginHref: loginHref,
      hosting: selectedHosting,
      onMediaViewerOpen: onMediaViewerOpen,
      onWatchAppToggle: onWatchAppToggle,
      onArtifactDownloadClick: onArtifactDownloadClick,
      onCloudMigrationResourceLinksClick: onCloudMigrationResourceLinksClick
    }),
    testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Overview),
    key: AppListingTab.Overview
  }); // 2 => Reviews Tab

  if (showReviewsTab) {
    tabs.push({
      label: 'Reviews',
      key: AppListingTab.Reviews,
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Reviews),
      content: /*#__PURE__*/React.createElement(ReviewsTab, {
        appId: appListing.appId,
        slug: appListing.slug,
        hosting: selectedHosting,
        appListingLink: appListingLink,
        onSeeMoreReviewsClick: onSeeMoreReviewsClick
      })
    });
  } // 3 => Pricing Tab


  if (showPricingTab && appListing.paymentModel && appListing.paymentModel !== MarketplaceAppPaymentModelValues.Free) {
    tabs.push({
      label: 'Pricing',
      content: /*#__PURE__*/React.createElement(PricingTab, {
        appId: appListing.appId,
        appKey: appListing.appKey,
        compatibleProducts: appListing.compatibleProducts,
        hasDataCenterVersion: appListing.hasDataCenterVersion,
        hosting: selectedHosting,
        learnMoreUrl: appListing.learnMoreUrl,
        marketingLabels: appListing.marketingLabels,
        partnerType: appListing.partner.partnerType,
        paymentModel: appListing.paymentModel,
        mpacBaseUrl: mpacBaseUrl,
        onPricingCalculatorInstallCTAClick: onPricingCalculatorInstallCTAClick
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Pricing),
      key: AppListingTab.Pricing
    });
  } // 4 => Support Tab


  tabs.push({
    label: 'Support',
    content: /*#__PURE__*/React.createElement(SupportTab, {
      supportDetails: appListing.supportDetails,
      mpacBaseUrl: mpacBaseUrl,
      atlassianCommunityBaseUrl: atlassianCommunityBaseUrl,
      hosting: selectedHosting,
      appKey: appListing.appKey,
      onSupportLinkClick: onSupportLinkClick
    }),
    testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Support),
    key: AppListingTab.Support
  }); // 5 => Versions Tab

  if (showVersionsTab) {
    tabs.push({
      label: 'Versions',
      content: /*#__PURE__*/React.createElement(VersionsTab, {
        versionHistoryUrl: appListing.versionHistoryUrl,
        hosting: selectedHosting,
        releaseDate: appListing.releaseDate,
        releaseNotes: appListing.releaseNotes,
        releaseSummary: appListing.releaseSummary,
        releaseNotesUrl: appListing.releaseNotesUrl,
        version: appListing.version,
        versionCount: appListing.versionCount,
        productCompatibilities: appListing.compatibleProducts
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Versions),
      key: AppListingTab.Versions
    });
  } // 6 => Installation Tab


  if (showInstallationTab) {
    tabs.push({
      label: 'Installation',
      content: /*#__PURE__*/React.createElement(InstallationTab, {
        appName: appListing.name,
        compatibleProducts: appListing.compatibleProducts,
        deploymentType: appListing.deploymentType,
        documentationUrl: appListing.documentationUrl,
        hosting: selectedHosting,
        installationInstructions: appListing.installationInstructions,
        paymentModel: appListing.paymentModel,
        versionCount: appListing.versionCount,
        versionHistoryUrl: appListing.versionHistoryUrl
      }),
      testId: TEST_ID.APP_LISTING_TAB(AppListingTab.Installation),
      key: AppListingTab.Installation
    });
  }

  return tabs;
};
export const selectedTabWithFallback = (tabs, selectedTab, onTabSelect) => {
  const tabSelected = tabs.find(tab => tab.key === selectedTab); // If we didn't find the selected tab key in the current list of available
  // tabs, fallback to the first tab, and notify that tab has changed

  if (!tabSelected) {
    onTabSelect(tabs[0].key);
    return tabs[0];
  }

  return tabSelected;
}; // eslint-disable-next-line max-lines-per-function

const AppListing = props => {
  var _appListing$programs, _appListing$programs$, _props$components, _props$components2;

  const {
    enableCloudFortifiedAppsDiscovery
  } = useFeatureFlags();
  if (!props.appDetails) return null;
  const {
    appDetails,
    links,
    headerInjection,
    selectedHosting,
    selectedTab,
    onTabSelect,
    mpacBaseUrl,
    atlassianCommunityBaseUrl,
    cloudMigration,
    loginHref,
    userLoggedIn,
    onHostingSelectOpen,
    onHostingOptionClick,
    onSupportLinkClick = () => {},
    onMediaViewerOpen = () => {},
    onWatchAppToggle = () => {},
    onSeeMoreReviewsClick = () => {},
    onArtifactDownloadClick = () => {},
    onContactPartnerClick = () => {},
    onCloudMigrationResourceLinksClick = () => {},
    onMigrationLozengeClick = () => {},
    onPricingCalculatorInstallCTAClick = () => {},
    showHeaderLozenges = false,
    showHeaderProductCompatibilities = false,
    showInstallationCount = false,
    showInstallationTab = false,
    showIntegrationsDetailsSection = false,
    showPricingTab = false,
    showResourcesSection = false,
    showReviewsTab = false,
    showVersionsTab = false,
    showCloudMigrationLozenge = false,
    openMarketplaceLinksInNewTab = true
  } = props;
  const appListing = transformAppListing({
    appDetails,
    mediaFileLink: links.mediaFile,
    partnerDetailsLink: links.partnerDetails,
    appVersionHistoryLink: links.appVersionHistory,
    artifactDownloadLink: links.artifactDownload,
    descriptorDownloadLink: links.descriptorDownload,
    licensePurchaseLink: links.licensePurchase,
    trialLicenseActivationLink: links.trialLicenseActivation,
    appIconLink: links.appIcon,
    hosting: selectedHosting
  });
  const appCompatibleWithDcEnabledProduct = MarketplaceAppUtils.maybeDCCompatible(appListing.productHostingOptions, appListing.serverCompatibleProducts);
  const appCompatibleWithCloudSupportedProduct = compatibleWithCloudSupportedProduct(appListing.compatibleProducts);
  const alternateCloudListingLink = cloudMigration !== null && cloudMigration !== void 0 && cloudMigration.alternateCloudAppKey ? links.appDetails.expand({
    appKey: cloudMigration.alternateCloudAppKey
  }) : null;
  const hostingSelect = props.showHostingSelect ? /*#__PURE__*/React.createElement(TabAfterContentDiv, null, /*#__PURE__*/React.createElement(HostingSelectContainer, null, /*#__PURE__*/React.createElement(HostingSelect, {
    productHostingOptions: appListing.productHostingOptions,
    selected: props.selectedHosting,
    appCompatibleWithDcEnabledProduct: appCompatibleWithDcEnabledProduct,
    appCompatibleWithCloudSupportedProduct: appCompatibleWithCloudSupportedProduct,
    onChange: props.onHostingChange,
    partnerSupportUrl: appListing.partner.support.url,
    alternateCloudListingLink: alternateCloudListingLink,
    onContactPartnerClick: onContactPartnerClick,
    onOpen: onHostingSelectOpen,
    onHostingOptionClick: onHostingOptionClick
  }))) : null;

  const handleTabChange = tabData => {
    // $FlowIgnore -- AppListingTabsType's key has a stronger string literal type, which is what we'll get back since its what we passed initially
    const tabKey = tabData.key;

    if (typeof onTabSelect === 'function') {
      onTabSelect(tabKey);
    }
  };

  const isAppCloudFortified = !!((_appListing$programs = appListing.programs) !== null && _appListing$programs !== void 0 && (_appListing$programs$ = _appListing$programs.cloudFortified) !== null && _appListing$programs$ !== void 0 && _appListing$programs$.isStatusApproved);
  const hasCloudFortifiedBadge = enableCloudFortifiedAppsDiscovery && isAppCloudFortified;
  const tabs = makeAppListingTabs({
    /* eslint sort-keys: "error" */
    appListing,
    appListingLink: links.appListing,
    selectedHosting,
    showInstallationTab,
    showIntegrationsDetailsSection,
    showResourcesSection,
    showCertificationSection: hasCloudFortifiedBadge,
    showReviewsTab,
    showVersionsTab,
    showPricingTab,
    mpacBaseUrl,
    atlassianCommunityBaseUrl,
    loginHref,
    userLoggedIn,
    onSupportLinkClick,
    onMediaViewerOpen,
    onWatchAppToggle,
    onSeeMoreReviewsClick,
    onArtifactDownloadClick,

    /* eslint sort-keys: "off" */
    cloudMigration,
    onCloudMigrationResourceLinksClick,
    onPricingCalculatorInstallCTAClick
  });
  const tabSelected = selectedTabWithFallback(tabs, selectedTab, onTabSelect);
  const supportTabLink = UrlUtils.updateQueryParam(window.location.href, 'tab', AppListingTab.Support);
  const iconLink = {
    expand: () => appListing.logo
  };

  const getAnalyticsContextData = () => ({
    deploymentType: appListing.deploymentType,
    hasCloudFortifiedBadge
  });

  return /*#__PURE__*/React.createElement(AnalyticsContext, {
    data: getAnalyticsContextData()
  }, /*#__PURE__*/React.createElement("div", null, (_props$components = props.components) === null || _props$components === void 0 ? void 0 : _props$components.beforeHeader, /*#__PURE__*/React.createElement(AppHeader, {
    appHeaderDetails: makeAppHeaderProps(appListing),
    appRatingLink: links.appRating,
    appIconLink: iconLink,
    bottomSeparator: false,
    headerInjection: headerInjection,
    isAppCloudFortified: isAppCloudFortified,
    showInstallationCount: showInstallationCount,
    openMarketplaceLinksInNewTab: openMarketplaceLinksInNewTab,
    compatibilitySummary: showHeaderProductCompatibilities ? /*#__PURE__*/React.createElement(ProductCompatibilitySummary, {
      cloudCompatibleProducts: appListing.cloudCompatibleProducts,
      serverCompatibleProducts: appListing.serverCompatibleProducts,
      dataCenterCompatibleProducts: appListing.dataCenterCompatibleProducts,
      totalVersionCount: appListing.totalVersionCount,
      versionHistoryUrl: appListing.versionHistoryUrl
    }) : null,
    lozenges: showHeaderLozenges ? makeHeaderLozenges({
      isSupported: appListing.supportDetails.isSupported,
      partnerName: appListing.partner.name,
      supportLink: supportTabLink,
      categories: appListing.categories,
      marketingLabels: appListing.marketingLabels,
      onMigrationLozengeClick,
      cloudMigration,
      showCloudMigrationLozenge
    }) : []
  }), (_props$components2 = props.components) === null || _props$components2 === void 0 ? void 0 : _props$components2.afterHeader, /*#__PURE__*/React.createElement(ResponsiveTabs, {
    id: "app-listing-tabs",
    selected: tabSelected.key,
    maxVisibleTabs: MAX_VISIBLE_TABS_CONFIG,
    onChange: handleTabChange,
    afterTabs: hostingSelect,
    tabs: tabs
  }), /*#__PURE__*/React.createElement(AppStructuredData, {
    name: appListing.name,
    createdAt: appListing.createdAt,
    description: appListing.summary,
    icon: appListing.logo,
    partner: {
      name: appListing.partner.name,
      url: appListing.partner.url
    },
    review: {
      rating: appListing.reviewSummary.rating,
      count: appListing.reviewSummary.count
    },
    compatibleProducts: appListing.compatibleProducts
  })));
};

export default AppListing;